import { Modal, Checkbox, Input } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setModalGameRule, setModalBuyTicket } from "@/app/modal/actions";
import "./styles.scss";
import { CiMail } from "react-icons/ci";

const LOCAL_STORAGE_KEY = "lucky_draw_rules_ack";

const ModalGameRule = () => {
  const dispatch = useDispatch();
  const modalGameRule = useSelector((state: any) => state.modal.modalGameRule);
  const {
    data: { rules },
    toggle,
  } = modalGameRule;
  const [checked, setChecked] = useState<boolean>(false);
  const [email, setEmail] = useState<string>("");
  const [hasConfirmedBefore, setHasConfirmedBefore] = useState<boolean>(false);

  useEffect(() => {
    if (!toggle) return;
    const hasConfirmed = localStorage.getItem(LOCAL_STORAGE_KEY) === "true";
    setChecked(hasConfirmed);
    setHasConfirmedBefore(hasConfirmed);
  }, [modalGameRule?.toggle, modalGameRule?.data?.rules]);

  const winnersPercents = useMemo(() => rules?.distribution?.winnersPercents || [], [rules]);

  const handleClose = () => {
    dispatch(setModalGameRule({ toggle: false }));
  };

  const handleConfirm = () => {
    if (!checked) return;
    localStorage.setItem(LOCAL_STORAGE_KEY, "true");
    setHasConfirmedBefore(true);
    if (modalGameRule?.data?.buyPayload) {
      dispatch(setModalBuyTicket(modalGameRule.data.buyPayload));
    }
    handleClose();
  };

  const handleButtonClick = () => {
    if (hasConfirmedBefore) {
      handleClose();
    } else {
      handleConfirm();
    }
  };

  const renderPercent = (value: any) => {
    if (value && typeof value === "object" && "percent" in value) {
      return value.percent;
    }
    return value;
  };

  const winnerMaxClaimDays = String(rules?.winnerMaxClaimDays || "5");

  return (
    <Modal
      title="Game Rule"
      visible={modalGameRule?.toggle}
      onCancel={handleClose}
      footer={false}
      width={800}
      centered
      style={{ top: 20 }}
      className="modal-game-rule"
    >
      <div className="flex flex-col gap-4 game-rule">
        <div className="text-sm">
          <div>
            <span className="font-bold text-[#2750FB]">Ticket Price:</span> : {rules?.ticketPrice} MBC / ticket
            &nbsp;&rarr;&nbsp;
            <span>Each ticket corresponds to a random lucky number.</span>
          </div>
          {rules?.salesPeriodText && (
            <div className="mt-2">
              <span className="font-bold text-[#2750FB]">Ticket Sales Period:</span> {rules?.salesPeriodText}
            </div>
          )}
          {rules?.drawTimeText && (
            <div className="mt-2">
              <span className="font-bold text-[#2750FB]">Draw Time:</span> {rules?.drawTimeText}
            </div>
          )}

          <div className="mt-3">
            <div className="font-bold text-[#2750FB]">Prize Pool Distribution:</div>
            <ul className="list-disc ml-4 mt-1">
              {winnersPercents.map((w: any, idx: number) => (
                <li key={idx}>
                  {w?.label || "Prize"}: <span className="font-bold">{w?.percent}%</span>
                </li>
              ))}
              <li>
                <span className="font-bold">{renderPercent(rules?.distribution?.nextRoundPercent)}%</span> rollover to the next round
              </li>
              <li>
                <span className="font-bold">{renderPercent(rules?.distribution?.opsPercent)}%</span> system (operations, marketing, human
                resources)
              </li>
              <li>
                <span className="font-bold">{renderPercent(rules?.distribution?.burnPercent)}%</span> burn
              </li>
              <li>
                <span className="font-bold">{renderPercent(rules?.distribution?.holdPercent)}%</span> dividend to stakeholders
              </li>
            </ul>
          </div>

          <div className="mt-3">
            <span className="font-bold text-[#2750FB]">Results:</span> Announced publicly.
          </div>
          <div className="mt-2">
            <span className="font-bold text-[#2750FB]">Ticket Purchase:</span> Players can buy multiple tickets to
            increase their chances of winning.
          </div>
        </div>

        {/* note */}
        <div className="mt-4 p-4 bg-red-50 rounded-lg">
          <div className="font-bold text-red-500 mb-3">Note:</div>
          <ul className="list-disc ml-5 space-y-2 text-sm">
            <li>Purchased tickets are non-refundable.</li>
            <li>
              After winning, players must <span className="font-bold text-red-500">"claim"</span> their reward to their
              wallet within <span className="font-bold text-red-500">{winnerMaxClaimDays} days</span> from the time the
              system announces the result.
            </li>
            <li>
              If the player fails to claim the reward within the specified period{" "}
              <span className="font-bold text-red-500">({winnerMaxClaimDays} days)</span>:{" "}
              <span className="font-bold">
                "The reward will be automatically returned to the Pool and added to the prize fund for upcoming rounds."
              </span>
            </li>
            <li>
              The system will not be responsible for handling any complaints or requests to reclaim rewards once they
              have been returned to the Pool.
            </li>
          </ul>
        </div>

        <div className="mt-1 flex items-center gap-2">
          <Checkbox
            checked={checked}
            onChange={e => setChecked(e.target.checked)}
            disabled={hasConfirmedBefore}
            className="game-rule-checkbox"
          >
            I confirm that I have read and understood the rules of the game.
          </Checkbox>
        </div>

        <div className="mt-2">
          <div className=" mb-1">Submit email for updates (optional):</div>
          <Input
            placeholder="Your e-mail"
            value={email}
            onChange={e => setEmail(e.target.value)}
            suffix={
              <span className="text-gray-400">
                <CiMail size={20} />
              </span>
            }
            className="game-rule-input-email"
          />
        </div>
        <span
          className="mt-2 text-[#2750FB] underline text-center cursor-pointer"
          onClick={() => {
            window.open("https://t.me/monbasevn", "_blank");
          }}
        >
          Join our community for game updates
        </span>

        <div className="flex gap-3 mt-4">
          <button
            className="flex-1 h-[42px] bg-[#2750FB] text-white rounded-md font-bold disabled:opacity-50"
            disabled={!checked}
            onClick={handleButtonClick}
          >
            {hasConfirmedBefore ? "Close" : "I Agree"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ModalGameRule;
